import {makeAutoObservable, runInAction} from 'mobx';
import {networkOptimization} from '../../../../lib/api';
import {isAbortError} from '../../../../lib/error-status';
import type {RosterSlot} from '../../../../lib/roster-colors';
import type {GeographyFilterOptions} from '../types';
import {rosterKey} from '../roster-label';
import {GeographyStore} from './geography-store';

export type RosterRef = {rosterId: string, revisionId: string};

// The adequacy standards the response reports, each roster meeting or missing each one
export type AdequacyStandard = 'time' | 'distance';

// What the comparison is read against: one standard, or both together
export type ComparisonMeasure = AdequacyStandard | 'combined';

// The user's open/collapsed choice for the summary panel; null until there is one
export type SummaryPanelPreference = 'open' | 'closed';

// A selected roster with its badge number (its slot)
export type SelectedRoster = RosterRef & {slot: RosterSlot};

// A selected roster with the number its per-roster response fields carry (its position in the request)
export type ResponseRoster = SelectedRoster & {fieldNumber: number};

// One row of the requirements checklist that gates the comparison
export type Requirement = {
    id: string;
    label: string;
    met: boolean;
    message?: string;
    // Rosters still needed to satisfy 'minimum-rosters'; drives the guidance sentence
    remaining?: number;
};

// Each badge number (1-5) is used by at most one roster at a time
const MAX_TOTAL_ROSTERS = 5;

const isSameRoster = (a: RosterRef, b: RosterRef) =>
    a.rosterId === b.rosterId && a.revisionId === b.revisionId;

// Splits the composite county value "CountyName-SSACD" at its last '-': county names can
// contain hyphens (e.g. "Miami-Dade"), while the SSACD is a numeric code
export const parseCountyValue = (value: string): {countyName: string, ssacd: string} => {
    const separator = value.lastIndexOf('-');
    return separator === -1
        ? {countyName: value, ssacd: ''}
        : {countyName: value.slice(0, separator), ssacd: value.slice(separator + 1)};
};

/**
 * RosterComparisonStore - Slot-based roster comparison (2-5 rosters)
 *
 * Manages comparison sessions with 1 primary roster + up to 4 comparison rosters.
 * Badges number the selected rosters 1..N in the order they were added, and removing one
 * renumbers the rest so there is never a gap. The primary is a role held by one of them:
 * changing it replaces the old primary, which leaves the selection (see setPrimary).
 * Uses separate comparison-specific filters (following DisruptionStore pattern), whose options are
 * loaded for the primary roster into the store's own GeographyStore, so the Network Analysis
 * filters keep the route roster's. Specialty is filtered client-side by the comparison table.
 *
 * Used by: rosterComparisonPage.tsx
 *
 * @see roster-store.ts - Legacy comparison logic (to be deprecated for comparison features)
 */
export class RosterComparisonStore {
    // Maximum number of comparison rosters (excluding primary roster)
    private static readonly MAX_COMPARISON_ROSTERS = MAX_TOTAL_ROSTERS - 1;

    // Core State - selected rosters in the order they were added (a roster's badge is its
    // position), and which of them holds the primary role
    private _rosters: RosterRef[] = [];
    private _primary: RosterRef | null = null;

    // Geography Grain Toggle (COUNTY or ZIP)
    private _geographyGrain: 'COUNTY' | 'ZIP' = 'COUNTY';

    // Comparison-Specific Filters (separate from global filters)
    private _comparisonFilters = {
        state: '',
        county: '',
        zip: '',
        standard_type: '',
        standard_version: '',
        patient_roster_id: ''
    };

    // Which roster the filter options in filterData are for (they don't vary by revision), and
    // the load in progress, if any
    private _filterOptionsRosterId: string | null = null;
    private _filterOptionsController: AbortController | null = null;

    // Shared by the comparison table and the summary panel, so the two always describe the same measure
    private _measure: ComparisonMeasure = 'combined';
    // Specialty names the comparison is narrowed to (empty shows them all), shared for the same reason
    private _selectedSpecialties: string[] = [];

    // Kept here rather than in the panel so it outlives a remount; reset() clears it per visit
    private _summaryPanelPreference: SummaryPanelPreference | null = null;
    // Whether the panel has opened by itself this visit; it does so once, when the requirements are first met
    private _summaryPanelAutoOpened = false;

    // Comparison Data & Loading
    comparisonData: Array<any> | null = null;
    // Rosters (in request order) that comparisonData was fetched for; column i of the data belongs to entry i
    comparisonDataRosters: Array<{rosterId: string, revisionId: string}> | null = null;
    loadingComparisonData = false;
    // Set when the last comparison request failed (not when it was aborted), cleared when the next one starts
    comparisonLoadFailed = false;
    // Controller of the comparison request in flight; a request it no longer matches has been replaced
    private comparisonRequest: AbortController | null = null;

    constructor(
        private showToastNotification: (kind: string, title: string, subtitle: string) => void,
        // Filter options for the primary roster
        private filterData: GeographyStore = new GeographyStore()
    ) {
        makeAutoObservable(this, {
            // Exclude these lookups from being wrapped as actions to ensure observable
            // dependencies are properly tracked when called from computed properties and renders
            getBlockingRequirement: false,
            getFilterValues: false
        });
    }

    // Getters

    /**
     * All selected rosters (primary and comparison) in slot order, numbered 1..N - the order
     * the chip group, Primary Roster select and comparison table show them in
     */
    get rostersBySlot(): SelectedRoster[] {
        return this._rosters.map(({rosterId, revisionId}, i) => ({
            rosterId,
            revisionId,
            slot: (i + 1) as RosterSlot
        }));
    }

    get primaryRoster(): SelectedRoster | null {
        const primary = this._primary;
        return primary ? this.rostersBySlot.find(r => isSameRoster(r, primary)) ?? null : null;
    }

    /**
     * Selected rosters other than the primary, in slot order
     */
    get comparisonRosters(): SelectedRoster[] {
        const primary = this._primary;
        return this.rostersBySlot.filter(r => !primary || !isSameRoster(r, primary));
    }

    /**
     * All selected rosters in API request order: the primary first, then comparison rosters
     * by slot. The comparison response numbers its per-roster fields in this order.
     */
    get rostersInRequestOrder(): SelectedRoster[] {
        const primary = this.primaryRoster;
        return primary ? [primary, ...this.comparisonRosters] : this.comparisonRosters;
    }

    /**
     * The selected rosters the loaded rows have figures for, in request order, each with the
     * number its response fields carry (timeAdequacyFlag1, ...). Numbered by the request the rows
     * were fetched for (comparisonDataRosters), not the live selection: a replaced request's late
     * response can land after the selection has changed. Until rows load, by the next request.
     * The comparison table and the summary panel both read the rows through this.
     */
    get responseRosters(): ResponseRoster[] {
        const fetchedFor = this.comparisonData && this.comparisonDataRosters
            ? this.comparisonDataRosters
            : this.rostersInRequestOrder;
        const fieldNumbers = new Map(fetchedFor.map((r, i) => [rosterKey(r.rosterId, r.revisionId), i + 1]));
        return this.rostersInRequestOrder.flatMap((roster) => {
            const fieldNumber = fieldNumbers.get(rosterKey(roster.rosterId, roster.revisionId));
            return fieldNumber ? [{...roster, fieldNumber}] : [];
        });
    }

    get geographyGrain() {
        return this._geographyGrain;
    }

    get comparisonFilters() {
        return this._comparisonFilters;
    }

    /**
     * Geography records the filters offer their values from, loaded for the primary roster
     */
    get filterRecords(): GeographyFilterOptions[] {
        return this.filterData.availableGeographyFilters;
    }

    get loadingFilterOptions(): boolean {
        return this._filterOptionsController !== null;
    }

    /**
     * The primary's roster while its filter options aren't loaded, null once they are (or with no
     * primary). It also comes back after reset() clears them for a primary whose roster is unchanged
     */
    get filterOptionsRosterToLoad(): string | null {
        const rosterId = this._primary?.rosterId;
        return rosterId && rosterId !== this._filterOptionsRosterId ? rosterId : null;
    }

    /**
     * Distinct values a filter offers from the primary roster's records, as
     * GeographyStore.getUniqueValuesByKey returns them (joined with separator, '-' by default,
     * when key lists several fields)
     */
    getFilterValues(
        key: string | string[],
        filter?: Record<string, string>,
        separator?: string,
        sortOrder?: 'ASC' | 'DESC'
    ): string[] {
        return this.filterData.getUniqueValuesByKey(key, filter, separator, sortOrder);
    }

    get measure(): ComparisonMeasure {
        return this._measure;
    }

    get selectedSpecialties(): string[] {
        return this._selectedSpecialties;
    }

    /**
     * The comparison rows for the selected specialties, as the table shows them and the summary
     * panel describes them; every row when none is selected, and null until data has loaded
     */
    get filteredComparisonData(): Array<any> | null {
        const selected = this._selectedSpecialties;
        if (!this.comparisonData || selected.length === 0) {
            return this.comparisonData;
        }
        return this.comparisonData.filter(row => selected.includes(row.specialtyName));
    }

    get summaryPanelPreference(): SummaryPanelPreference | null {
        return this._summaryPanelPreference;
    }

    get summaryPanelAutoOpened(): boolean {
        return this._summaryPanelAutoOpened;
    }

    // Counts every selected roster, so all 5 can be added before a primary is chosen
    get canAddRoster(): boolean {
        return this._rosters.length < MAX_TOTAL_ROSTERS;
    }

    /**
     * Maximum number of rosters in a session (primary + comparison rosters)
     */
    get maxTotalRosters(): number {
        return MAX_TOTAL_ROSTERS;
    }

    get canRemoveRoster(): boolean {
        return this.comparisonRosters.length > 0;
    }

    get isReady(): boolean {
        return this.getBlockingRequirement().isReady;
    }

    // Includes rosters selected before a primary is chosen
    get totalRosterCount(): number {
        return this._rosters.length;
    }

    /**
     * Generates a unique key representing current roster selection
     * Used to trigger re-renders when rosters change (even if count stays same)
     * @returns string key combining all roster IDs
     */
    get rosterSelectionKey(): string {
        const primary = this._primary ? `${this._primary.rosterId}-${this._primary.revisionId}` : '';
        const comparisons = this.comparisonRosters
            .map(r => `${r.rosterId}-${r.revisionId}`)
            .join(',');
        return `${primary}|${comparisons}`;
    }

    /**
     * Validates comparison requirements (two-tier state machine gate)
     * Requirements:
     * 1. Primary roster selected
     * 2. At least 2 total rosters (primary + 1 comparison) for meaningful comparison
     * 3. County geography filter selected
     *
     * Used by UI to show requirements checklist with status glyphs:
     * - Green tick = requirement met
     * - Hollow ring = requirement not met
     *
     * @returns object with requirement status and messages for UI checklist
     */
    getBlockingRequirement(): {isReady: boolean; requirements: Requirement[]} {
        // Extract county name from composite "CountyName-SSACD" format
        const {countyName} = parseCountyValue(this._comparisonFilters.county);

        // Check each requirement status
        const hasPrimaryRoster = this._primary !== null;
        const hasMinimumRosters = this.totalRosterCount >= 2;
        const hasCounty = this._comparisonFilters.county !== '';
        const hasZip = this._comparisonFilters.zip !== '';

        // The geography requirement follows the grain: switching grain clears the other filter
        const geographyRequirement = this._geographyGrain === 'ZIP'
            ? {
                id: 'zip-selected',
                label: 'ZIP code',
                met: hasZip,
                message: this._comparisonFilters.zip || 'Not selected'
            }
            : {
                id: 'county-selected',
                label: 'County',
                met: hasCounty,
                message: countyName || 'Not selected'
            };

        const requirements = [
            {
                id: 'primary-roster',
                label: 'Primary roster',
                met: hasPrimaryRoster,
                message: hasPrimaryRoster
                    ? `Roster ${this._primary!.rosterId} (Rev ${this._primary!.revisionId})`
                    : 'Not selected',
            },
            {
                id: 'minimum-rosters',
                label: 'At least 2 rosters',
                met: hasMinimumRosters,
                message: this.totalRosterCount === 0 ? 'Not selected' : `${this.totalRosterCount} selected`,
                remaining: Math.max(0, 2 - this.totalRosterCount)
            },
            geographyRequirement
        ];

        // Additional validation: max comparison rosters
        const withinMaxLimit = this.comparisonRosters.length <= RosterComparisonStore.MAX_COMPARISON_ROSTERS;

        return {
            isReady: requirements.every(r => r.met) && withinMaxLimit,
            requirements
        };
    }

    /**
     * Legacy compatibility method - returns boolean only
     * @deprecated Use getBlockingRequirement() for full requirement details
     */
    isBlocked(): boolean {
        return !this.getBlockingRequirement().isReady;
    }

    /**
     * Gives the primary role to a roster (the roster picked in My Files when the page opens, then
     * whichever of its revisions is chosen with the Primary Roster select), replacing the current
     * primary, which leaves the selection.
     * A selected comparison roster becomes the primary where it is, and the rosters after the old
     * primary move down a slot. A roster that isn't selected yet takes the old primary's slot, or,
     * with no primary yet, is added after the last roster.
     *
     * @returns true if the roster is now primary, false if rejected (missing IDs, or all slots taken
     * with no primary to replace)
     */
    setPrimary(rosterId: string, revisionId: string): boolean {
        if (!rosterId || !revisionId) {
            this.showToastNotification('error', 'Invalid Roster', 'Roster ID and Revision ID are required.');
            return false;
        }

        const roster = {rosterId, revisionId};
        const previous = this._primary;
        if (previous && isSameRoster(previous, roster)) {
            return true;
        }

        // Replacing the primary never adds a roster, so only a first primary can exceed the limit
        const isSelected = this._rosters.some(r => isSameRoster(r, roster));
        if (!isSelected && !previous && this._rosters.length >= MAX_TOTAL_ROSTERS) {
            this.showToastNotification('warning', 'Maximum Rosters Selected', 'Remove one to add another.');
            return false;
        }

        runInAction(() => {
            if (isSelected) {
                if (previous) {
                    this._rosters = this._rosters.filter(r => !isSameRoster(r, previous));
                }
            } else if (previous) {
                this._rosters = this._rosters.map(r => (isSameRoster(r, previous) ? roster : r));
            } else {
                this._rosters = [...this._rosters, roster];
            }
            this._primary = roster;
            // The request lists the primary first, so loaded data no longer lines up with the rosters
            this.comparisonData = null;
        });

        return true;
    }

    /**
     * Clears the primary roster (it leaves the selection; later rosters are renumbered)
     */
    clearPrimary(): void {
        const primary = this._primary;
        runInAction(() => {
            if (primary) {
                this._rosters = this._rosters.filter(r => !isSameRoster(r, primary));
                this.comparisonData = null;
            }
            this._primary = null;
        });
    }

    /**
     * Adds a comparison roster after the last selected roster (taking the next slot)
     * Rejects with a toast if already at max capacity (5 rosters total) or roster already exists
     *
     * @param rosterId - Roster ID to add
     * @param revisionId - Revision ID to add
     * @returns true if added successfully, false if rejected (at capacity or duplicate)
     */
    addRoster(rosterId: string, revisionId: string): boolean {
        // Guard: validate roster ID and revision ID
        if (!rosterId || !revisionId) {
            this.showToastNotification('error', 'Invalid Roster', 'Roster ID and Revision ID are required.');
            return false;
        }

        // Guard: max rosters (5 in total, primary included)
        if (!this.canAddRoster) {
            this.showToastNotification('warning', 'Maximum Rosters Selected', 'Remove one to add another.');
            return false;
        }

        // Guard: prevent duplicate roster
        const isDuplicate = this.isRosterInComparison(rosterId, revisionId);

        if (isDuplicate) {
            this.showToastNotification('warning', 'Roster Already Selected', 'This roster is already in the comparison.');
            return false;
        }

        // Guard: prevent adding primary roster as comparison
        if (this._primary && isSameRoster(this._primary, {rosterId, revisionId})) {
            this.showToastNotification('warning', 'Cannot Add Primary Roster', 'The primary roster cannot be added as a comparison roster.');
            return false;
        }

        runInAction(() => {
            this._rosters = [...this._rosters, {rosterId, revisionId}];
            // Clear stale comparison data when roster list changes
            this.comparisonData = null;
        });

        return true;
    }

    /**
     * Removes a comparison roster by index
     * Rosters after it move down a slot, so the numbers stay 1..N with no gap
     *
     * @param index - Zero-based index (0-3) of roster to remove
     * @returns true if removed, false if invalid index
     */
    removeRoster(index: number): boolean {
        const removed = this.comparisonRosters[index];

        // Guard: valid index
        if (!removed) {
            return false;
        }

        runInAction(() => {
            // Assign a new array (as addRoster does) so reference-based memo dependencies see the change
            this._rosters = this._rosters.filter(r => !isSameRoster(r, removed));
            // Clear stale comparison data
            this.comparisonData = null;
        });

        return true;
    }

    /**
     * Removes all comparison rosters
     */
    clearAllComparisons(): void {
        const primary = this._primary;
        runInAction(() => {
            this._rosters = primary ? this._rosters.filter(r => isSameRoster(r, primary)) : [];
            this.comparisonData = null;
        });
    }

    /**
     * Checks if a specific roster is in the comparison list
     * @param rosterId - Roster ID to check
     * @param revisionId - Revision ID to check
     * @returns true if roster is in comparison list
     */
    isRosterInComparison(rosterId: string, revisionId: string): boolean {
        return this.comparisonRosters.some(r => isSameRoster(r, {rosterId, revisionId}));
    }

    /**
     * Gets the slot (badge number, 1-5) for a given roster
     * @returns slot if the roster is primary or in comparison, null otherwise
     */
    getRosterSlot(rosterId: string, revisionId: string): RosterSlot | null {
        const roster = this.rostersBySlot.find(r => isSameRoster(r, {rosterId, revisionId}));
        return roster ? roster.slot : null;
    }

    /**
     * Gets the list index (0-based) for a given comparison roster
     * @returns index if found, -1 if not found
     */
    getRosterIndex(rosterId: string, revisionId: string): number {
        return this.comparisonRosters.findIndex(r => isSameRoster(r, {rosterId, revisionId}));
    }

    /**
     * Sets the geography grain (COUNTY or ZIP)
     * Clears geography-specific filter (county or zip) when grain changes
     * Keeps comparison rosters and other filters (state, standard_type, etc.) selected
     *
     * @param grain - 'COUNTY' or 'ZIP'
     */
    setGeographyGrain(grain: 'COUNTY' | 'ZIP'): void {
        if (this._geographyGrain === grain) return;

        runInAction(() => {
            this._geographyGrain = grain;
            // Clear the geography-specific filter based on the NEW grain
            // When switching TO County, clear ZIP; when switching TO ZIP, clear County
            // Assign a new object (as setComparisonFilter does) so reference-based dependencies see the change
            this._comparisonFilters = {
                ...this._comparisonFilters,
                ...(grain === 'COUNTY' ? {zip: ''} : {county: ''}),
            };
            // Clear comparison data when grain changes
            this.comparisonData = null;
            // NOTE: Does NOT clear comparison rosters - they remain selected
        });
    }

    /**
     * Sets a comparison filter value, resetting the filters that depend on it, as
     * GeographyStore.setGeographyFilter does for the other filter panels:
     * - Adequacy standard: version and patient roster go to their first options; state, county and ZIP clear
     * - Standard version: patient roster goes to its first option; state, county and ZIP clear
     * - Patient roster: state, county and ZIP clear
     * - State: county and ZIP clear
     * Any change clears the loaded comparison data; setting the current value does nothing
     *
     * @param filterName - Filter field name
     * @param value - Filter value
     */
    setComparisonFilter(filterName: keyof typeof this._comparisonFilters, value: string): void {
        if (this._comparisonFilters[filterName] === value) {
            return;
        }

        const filters = {...this._comparisonFilters, [filterName]: value};
        switch (filterName) {
        case 'standard_type':
            filters.standard_version = this.getDefaultStandardVersion(value);
            filters.patient_roster_id = this.getDefaultPatientRosterId(value, filters.standard_version);
            Object.assign(filters, {state: '', county: '', zip: ''});
            break;
        case 'standard_version':
            filters.patient_roster_id = this.getDefaultPatientRosterId(filters.standard_type, value);
            Object.assign(filters, {state: '', county: '', zip: ''});
            break;
        case 'patient_roster_id':
            Object.assign(filters, {state: '', county: '', zip: ''});
            break;
        case 'state':
            Object.assign(filters, {county: '', zip: ''});
            break;
        }

        runInAction(() => {
            this._comparisonFilters = filters;
            // Clear comparison data when filters change
            this.comparisonData = null;
        });
    }

    /**
     * First standard version for an adequacy standard, as the Standard Version filter lists them
     * @private
     */
    private getDefaultStandardVersion(standardType: string): string {
        return this.getFilterValues('standard_version', {standard_type: standardType}, '', 'DESC')[0] || '';
    }

    /**
     * First patient roster ID for a standard and version, as the Patient Roster filter lists them
     * @private
     */
    private getDefaultPatientRosterId(standardType: string, standardVersion: string): string {
        // Fetch composite "id-name" and extract just the ID part
        const composite = this.getFilterValues(
            ['patient_roster_id', 'patient_roster_name'],
            {standard_type: standardType, standard_version: standardVersion},
            '-'
        )[0];
        // Extract ID (before the dash)
        return composite ? composite.split('-')[0] : '';
    }

    /**
     * Sets the measure the comparison is read against
     * Keeps comparison data: the response carries both measures, so nothing needs reloading
     *
     * @param measure - 'combined', 'time' or 'distance'
     */
    setMeasure(measure: ComparisonMeasure): void {
        this._measure = measure;
    }

    /**
     * Narrows the comparison to these specialties, by name; an empty list shows every specialty
     * Keeps comparison data: specialty is filtered client-side, so nothing needs reloading
     *
     * @param names - Specialty names, as the response's specialtyName gives them
     */
    setSelectedSpecialties(names: string[]): void {
        this._selectedSpecialties = names;
    }

    /**
     * Records whether the summary panel is open or collapsed, so it stays as the user left it
     *
     * @param preference - 'open' or 'closed'
     */
    setSummaryPanelPreference(preference: SummaryPanelPreference): void {
        this._summaryPanelPreference = preference;
    }

    /**
     * Records the panel's one auto-open of the visit, as an open preference. It replaces any
     * choice made before the requirements were met, when there was nothing to summarise
     */
    recordSummaryPanelAutoOpen(): void {
        this._summaryPanelAutoOpened = true;
        this._summaryPanelPreference = 'open';
    }

    /**
     * Looks up the code of the selected state. The State filter holds the state name
     * (state_additional_info.state), while the API takes the record's state code, as
     * GeographyStore sends for the other filter panels
     *
     * @returns the state code, or '' when no state is selected or it isn't in the geography data
     * @private
     */
    private getStateCode(): string {
        const stateName = this._comparisonFilters.state;
        // An empty filter value matches every record, so only look up a selected state
        if (!stateName) {
            return '';
        }

        return this.getFilterValues('state', {'state_additional_info.state': stateName})[0] || '';
    }

    /**
     * Builds the filter payload for API calls, matching the format used by the old implementation
     * Sends the state code and the ssacd from the composite county value, using the correct field names
     * Includes geography-specific fields based on current geographyGrain setting
     *
     * @private
     */
    private buildFilterPayload(): Record<string, any> {
        // Base payload with common filters
        const payload: Record<string, any> = {
            state: this.getStateCode(),
            standard_type: this._comparisonFilters.standard_type || '',
            standard_version: this._comparisonFilters.standard_version || '',
            patient_roster_id: this._comparisonFilters.patient_roster_id || ''
        };

        // Add geography-specific fields based on grain
        if (this._geographyGrain === 'COUNTY') {
            // Extract ssacd from composite county value "CountyName-SSACD"
            payload.county = parseCountyValue(this._comparisonFilters.county).ssacd;
        } else if (this._geographyGrain === 'ZIP') {
            payload.zip = this._comparisonFilters.zip || '';
        }

        return payload;
    }

    async loadNetworkAdequacyComparison(
        runId: number,
        controller: AbortController
    ): Promise<void> {
        if (!this.isReady) {
            throw new Error('Cannot load comparison: requirements not met. Use getBlockingRequirement() to check status.');
        }

        runInAction(() => {
            this.comparisonRequest = controller;
            this.loadingComparisonData = true;
            this.comparisonLoadFailed = false;
        });

        try {
            // Build roster objects array: [primary, comparison1, comparison2, ...]
            // Each roster is an object with rosterId and revisionId properties
            // Slot is UI identity only - not part of the API contract
            const selectedRosters = this.rostersInRequestOrder.map(
                ({rosterId, revisionId}) => ({rosterId, revisionId})
            );

            // Build filter payload matching old implementation format
            const filterPayload = this.buildFilterPayload();

            // Specialty is filtered client-side; the API still expects the field, so send it empty
            const requestFilters: Record<string, any> = {
                ...filterPayload,
                selectedRosters: selectedRosters,
                specialty: ''
            };

            // Call API
            const response = await networkOptimization.fetchNetworkAdequacyComparison(
                requestFilters,
                runId,
                controller.signal
            );

            // A replaced request no longer owns the data or the status: its late response
            // would describe a selection that no longer applies
            if (this.comparisonRequest !== controller) {
                return;
            }
            runInAction(() => {
                this.comparisonData = response;
                this.comparisonDataRosters = selectedRosters;
                this.loadingComparisonData = false;
                this.comparisonRequest = null;
            });
        } catch (error) {
            // The abort of a replaced request settles after its successor has started, so it
            // must not mark that successor as finished
            if (this.comparisonRequest !== controller) {
                return;
            }
            // Silently handle abort errors - component unmounted or navigation occurred
            const aborted = error instanceof Error && isAbortError(error);
            runInAction(() => {
                this.loadingComparisonData = false;
                this.comparisonLoadFailed = !aborted;
                this.comparisonRequest = null;
            });
            if (aborted) {
                return;
            }
            throw error;
        }
    }

    /**
     * Loads the filter options for the primary roster, then keeps each selected filter value they
     * still offer and resets the rest (see reconcileFilters). The options depend on the roster
     * alone, so nothing loads when they are already loaded for the primary's roster.
     * Errors (including aborts) propagate so the caller can show a notification
     *
     * @param controller - AbortController for cancellation
     */
    async loadFilterOptions(controller: AbortController): Promise<void> {
        const rosterId = this.filterOptionsRosterToLoad;
        if (!rosterId) {
            return;
        }

        runInAction(() => {
            this._filterOptionsController = controller;
        });
        try {
            await this.filterData.loadGeographyData(Number(rosterId), controller);
            runInAction(() => {
                this._filterOptionsRosterId = rosterId;
                this.reconcileFilters();
            });
        } finally {
            runInAction(() => {
                // A newer load may have started since; only the current one ends the loading state
                if (this._filterOptionsController === controller) {
                    this._filterOptionsController = null;
                }
            });
        }
    }

    /**
     * Keeps each selected filter value the loaded options still offer and resets the rest, in
     * filter order since each list depends on the values before it: the adequacy standard, version
     * and patient roster go to their first options, and the state, county and ZIP to blank. With
     * nothing selected yet, this sets the defaults. Any change clears the loaded comparison data
     * @private
     */
    private reconcileFilters(): void {
        const current = this._comparisonFilters;
        const keepOrFirst = (value: string, options: string[]) =>
            options.includes(value) ? value : options[0] || '';
        const keepOrClear = (value: string, options: string[]) =>
            options.includes(value) ? value : '';

        const standard_type = keepOrFirst(current.standard_type, this.getFilterValues('standard_type'));
        const standard_version = keepOrFirst(
            current.standard_version,
            this.getFilterValues('standard_version', {standard_type}, '', 'DESC')
        );
        // The options are composite "id-name"; the filter holds the ID
        const patient_roster_id = keepOrFirst(
            current.patient_roster_id,
            this.getFilterValues(
                ['patient_roster_id', 'patient_roster_name'],
                {standard_type, standard_version},
                '-'
            ).map(option => option.split('-')[0])
        );
        const state = keepOrClear(
            current.state,
            this.getFilterValues('state_additional_info.state', {patient_roster_id, standard_type, standard_version})
        );
        // The County and ZIP filters list the values for the standard, patient roster and state
        const geographyArgs = {standard_type, standard_version, 'state_additional_info.state': state, patient_roster_id};
        const county = keepOrClear(
            current.county,
            this.getFilterValues(['county_additional_info.county_name', 'ssacd'], geographyArgs)
        );
        const zip = keepOrClear(current.zip, this.getFilterValues('zip', geographyArgs));

        const filters = {state, county, zip, standard_type, standard_version, patient_roster_id};
        const changed = (Object.keys(filters) as Array<keyof typeof filters>).some(k => filters[k] !== current[k]);
        if (changed) {
            this._comparisonFilters = filters;
            this.comparisonData = null;
        }
    }

    /**
     * Resets the store to initial state
     * Called on unmount or when navigating away from comparison page
     */
    reset(): void {
        runInAction(() => {
            this._primary = null;
            this._rosters = [];
            this._geographyGrain = 'COUNTY';
            this._comparisonFilters = {
                state: '',
                county: '',
                zip: '',
                standard_type: '',
                standard_version: '',
                patient_roster_id: ''
            };
            this._measure = 'combined';
            this._selectedSpecialties = [];
            this._summaryPanelPreference = null;
            this._summaryPanelAutoOpened = false;
            this.comparisonData = null;
            this.comparisonDataRosters = null;
            this.loadingComparisonData = false;
            this.comparisonLoadFailed = false;
            this.comparisonRequest = null;
            // The next primary loads its own filter options
            this._filterOptionsRosterId = null;
            this.filterData.reset();
        });
    }

    /**
     * Clears comparison data only (keeps roster selection)
     * Useful when filters change and data needs to be reloaded
     */
    clearComparisonData(): void {
        runInAction(() => {
            this.comparisonData = null;
        });
    }
}
